import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Star, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CompanyPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [company, setCompany] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchCompanyData = useCallback(async () => {
    try {
      const [companyRes, reviewsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/companies/${id}`),
        axios.get(`${BACKEND_URL}/api/reviews/company/${id}`)
      ]);
      setCompany(companyRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      toast.error(t('toast.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchCompanyData();
  }, [fetchCompanyData]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/reviews`, {
        company_id: id,
        rating,
        comment,
        reviewer_name: reviewerName || t('company.anonymous')
      });
      if (rating >= 4) {
        toast.success(t('toast.reviewPublished', 'Your review has been published!'));
        fetchCompanyData();
      } else {
        toast.success(t('toast.reviewSubmitted'));
      }
      setComment('');
      setReviewerName('');
      setRating(5);
    } catch (error) {
      toast.error(t('toast.failedToSubmit'));
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'fill-yellow-500 text-yellow-500' : 'text-slate-300'}`}
          />
        ))}
      </div>
    );
  };

  const companyStructuredData = useMemo(() => {
    if (!company) return null;
    return {
      "@context": "https://schema.org",
      "@type": "FinancialService",
      "name": company.name,
      "description": company.description,
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": company.average_rating,
        "reviewCount": company.review_count,
        "bestRating": 5,
        "worstRating": 1
      },
      "review": reviews.slice(0, 5).map(r => ({
        "@type": "Review",
        "author": { "@type": "Person", "name": r.reviewer_name },
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": r.rating,
          "bestRating": 5,
          "worstRating": 1
        },
        "reviewBody": r.comment
      }))
    };
  }, [company, reviews]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-lg text-slate-600">{t('common.loading')}</div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-lg text-slate-600">Company not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title={`${company.name} Recensioni e Opinioni - Broker Forex`}
        description={`Leggi ${company.review_count} recensioni verificate su ${company.name}. Valutazione: ${company.average_rating.toFixed(1)}/5. Scopri opinioni su spread, commissioni, depositi e prelievi.`}
        keywords={`${company.name} recensioni, ${company.name} opinioni, ${company.name} broker, ${company.name} affidabile, ${company.name} truffa`}
        ogType="product"
        structuredData={companyStructuredData}
      />
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Link to="/" className="inline-flex items-center text-blue-900 hover:text-blue-800" data-testid="back-to-home">
            <ArrowLeft className="w-5 h-5 mr-2" />
            <span className="font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>{t('common.backToHome')}</span>
          </Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Company Header */}
        <div className="bg-white border border-slate-200 rounded-lg p-8 mb-8 shadow-sm">
          <div className="flex items-start gap-6">
            {company.logo_url && (
              <img
                src={company.logo_url}
                alt={company.name}
                className="w-20 h-20 rounded-lg object-cover"
              />
            )}
            <div className="flex-1">
              <h1 className="text-4xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {company.name}
              </h1>
              <div className="flex items-center gap-4 mb-4">
                {renderStars(Math.round(company.average_rating))}
                <span className="text-2xl font-bold text-slate-900">
                  {company.average_rating.toFixed(1)}
                </span>
                <span className="text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  ({company.review_count} {t('company.reviews')})
                </span>
              </div>
              <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {company.description}
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Reviews */}
          <div className="lg:col-span-2 space-y-6">
            <h2 className="text-3xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t('company.userReviews')}
            </h2>
            
            {reviews.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
                <p className="text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {t('company.noReviews')}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div
                    key={review.id}
                    data-testid={`review-${review.id}`}
                    className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        {review.reviewer_name}
                      </span>
                      {renderStars(review.rating)}
                    </div>
                    <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      {review.comment}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Review Sidebar */}
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm sticky top-24">
              <h3 className="text-2xl font-black text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {t('company.submitReview')}
              </h3>
              
              <form onSubmit={handleSubmitReview} className="space-y-4">
                <div>
                  <Label htmlFor="reviewer-name" className="text-slate-700">
                    {t('company.yourName')}
                  </Label>
                  <Input
                    id="reviewer-name"
                    data-testid="reviewer-name-input"
                    value={reviewerName}
                    onChange={(e) => setReviewerName(e.target.value)}
                    placeholder={t('company.anonymous')}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="rating" className="text-slate-700">
                    {t('company.rating')}
                  </Label>
                  <select
                    id="rating"
                    data-testid="rating-select"
                    value={rating}
                    onChange={(e) => setRating(parseInt(e.target.value))}
                    className="mt-1 w-full bg-white border border-slate-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-900"
                  >
                    <option value="5">{t('company.ratingOptions.5')}</option>
                    <option value="4">{t('company.ratingOptions.4')}</option>
                    <option value="3">{t('company.ratingOptions.3')}</option>
                    <option value="2">{t('company.ratingOptions.2')}</option>
                    <option value="1">{t('company.ratingOptions.1')}</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="comment" className="text-slate-700">
                    {t('company.yourReview')}
                  </Label>
                  <Textarea
                    id="comment"
                    data-testid="comment-textarea"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    required
                    rows={6}
                    className="mt-1"
                    placeholder={t('company.sharePlaceholder')}
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="submit-review-btn"
                  disabled={submitting}
                  className="w-full bg-blue-900 text-white hover:bg-blue-800"
                >
                  {submitting ? t('company.submitting') : t('company.submitButton')}
                </Button>

                <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {t('company.positiveNote', 'Positive reviews (4-5 stars) are published immediately.')}
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyPage;
